import type { ApiResponse } from '../../api/contract';

type AuditLogApiResponse = ApiResponse<'/api/v1/audit-logs/{id}', 'get'>;
type SecurityEventApiResponse = ApiResponse<'/api/v1/security-events/{id}', 'get'>;

export type AuditLog = AuditLogApiResponse['data'];
export type AuditLogSummary = AuditLog;
export type AuditLogDetail = AuditLog;
export type AuditLogRole = NonNullable<AuditLog['principalRole']>;

export type SecurityEvent = SecurityEventApiResponse['data'];
export type SecurityEventSummary = SecurityEvent;
export type SecurityEventDetail = SecurityEvent;
export type SecurityEventSeverity = SecurityEvent['severity'];

export interface AuditLogFilters {
  readonly principalActorId: string;
  readonly principalRole: AuditLogRole | '';
  readonly partnerId: string;
  readonly agentId: string;
  readonly requestId: string;
  readonly sourceIp: string;
  readonly from: string;
  readonly to: string;
  readonly action: string;
  readonly entityType: string;
  readonly entityId: string;
}

export interface AuditLogQuery {
  readonly cursor: string;
  readonly pageSize: number;
  readonly filters: AuditLogFilters;
}

export interface SecurityEventFilters {
  readonly principalActorId: string;
  readonly principalRole: AuditLogRole | '';
  readonly partnerId: string;
  readonly agentId: string;
  readonly requestId: string;
  readonly sourceIp: string;
  readonly from: string;
  readonly to: string;
  readonly credentialActorId: string;
  readonly category: string;
  readonly severity: SecurityEventSeverity | '';
  readonly eventCode: string;
}

export interface SecurityEventQuery {
  readonly cursor: string;
  readonly pageSize: number;
  readonly filters: SecurityEventFilters;
}

/**
 * Literal placeholder rendered for any before/after or metadata value that is not a plain
 * string, number, or boolean. The backend already masks sensitive values before this reaches
 * the client; this is a defense-in-depth guard so the UI never recursively expands an unknown
 * nested shape (which could otherwise leak something the backend did not intend to expose).
 */
export const MASKED_OR_UNAVAILABLE = '[masked or unavailable]';

export interface BeforeAfterEntry {
  readonly key: string;
  /** null means the key was not present on this side (not the same as a masked value). */
  readonly before: string | null;
  readonly after: string | null;
}

export function beforeAfterEntries(before: unknown, after: unknown): readonly BeforeAfterEntry[] {
  const beforeRecord = asSafeRecord(before);
  const afterRecord = asSafeRecord(after);
  const keys = new Set<string>([
    ...(beforeRecord === null ? [] : Object.keys(beforeRecord)),
    ...(afterRecord === null ? [] : Object.keys(afterRecord)),
  ]);
  return [...keys].sort().map((key) => ({
    key,
    before: sideValue(beforeRecord, key),
    after: sideValue(afterRecord, key),
  }));
}

export interface SafeEntry {
  readonly key: string;
  readonly value: string;
}

/** Renders a masked, already-server-filtered key/value bag (e.g. metadata or details). */
export function safeEntries(record: unknown): readonly SafeEntry[] {
  const safeRecord = asSafeRecord(record);
  if (safeRecord === null) return [];
  return Object.keys(safeRecord)
    .sort()
    .map((key) => ({ key, value: safeScalar(safeRecord[key]) }));
}

function sideValue(record: Readonly<Record<string, unknown>> | null, key: string): string | null {
  if (record === null || !(key in record)) return null;
  return safeScalar(record[key]);
}

function safeScalar(value: unknown): string {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return MASKED_OR_UNAVAILABLE;
}

function asSafeRecord(value: unknown): Readonly<Record<string, unknown>> | null {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  return value as Readonly<Record<string, unknown>>;
}
